import React from "react";
import { Link } from "react-router-dom";
import Ornament from "./components/Ornament";
import Icon from "./components/Icon";
import "./SwadharmaApps.css";

/**
 * SwadharmaApps — directory of every service module in the Swadharma family.
 *
 * Status legend:
 *   live     — publicly reachable
 *   this     — you are here (current app)
 *   dev      — under active development
 *   planned  — committed roadmap, not yet started
 */
const MODULES = [
    {
        n: 1,
        name: "Shabda",
        sa: "शब्द",
        gloss: "sound · word",
        tag: "Sanskrit Teaching",
        blurb: "Structured Sanskrit instruction at all levels — from beginner śikṣā to advanced vyākaraṇa, śāstra and Vedic adhyayana.",
        status: "live",
        href: "https://shabda-web-404772120601.asia-south1.run.app/",
    },
    {
        n: 2,
        name: "Vitta Fin",
        sa: "वित्त",
        gloss: "wealth · finance",
        tag: "Accounting Tool",
        blurb: "Lightweight book-keeping and accounting tailored for temples, pāṭhaśālās, dharmik trusts and small Dharmik enterprises.",
        status: "live",
        href: "https://vitta-7f675.web.app/",
    },
    {
        n: 3,
        name: "MANI",
        sa: "मणि",
        gloss: "jewel",
        tag: "Recipe · Inventory · Nutrition",
        blurb: "Recipe planner, kitchen inventory and nutritional analysis — household and institutional kitchens, festival and satram cooking.",
        status: "live",
        href: "https://mani.vkg.works/",
    },
    {
        n: 4,
        name: "Viyat",
        sa: "वियत्",
        gloss: "sky · ether",
        tag: "Astrology Assistant",
        blurb: "Pañcāṅga, muhūrta and jyotiṣa workbench for traditional practitioners and learners — calculation aid, not prediction service.",
        status: "live",
        href: "https://viyat-3fcf1.web.app/#/home",
    },
    {
        n: 5,
        name: "Samudwaaha",
        sa: "समुद्वाह",
        gloss: "joining together",
        tag: "Marriage Portal & Counselling",
        blurb: "Dharmik matrimonial portal with values-aligned matchmaking and pre-marital counselling.",
        status: "live",
        href: "https://samudwaaha-web-i6kabgnndq-el.a.run.app/",
    },
    {
        n: 6,
        name: "Samudyoga",
        sa: "समुद्योग",
        gloss: "collective enterprise",
        tag: "Professional Services & Job Portal",
        blurb: "Connecting Dharmik talent — scholars, priests, professionals, creators — to institutions, families and Dharmik enterprises worldwide.",
        status: "this",
        href: "/",
    },
    {
        n: 7,
        name: "Dharma Satram",
        sa: "धर्म सत्रम्",
        gloss: "dharmik hospitality",
        tag: "Rituals · Food · Accommodation",
        blurb: "Personal rituals, food services and accommodation for pilgrims and yajamānas — currently operated as Sri Sumvid Manvantara (SSM).",
        status: "live",
        href: "https://swadharma-service-332912113546.asia-south1.run.app/",
    },
    {
        n: 8,
        name: "Namadheya",
        sa: "नामधेय",
        gloss: "appellation · naming",
        tag: "Naming Convention",
        blurb: "Traditional Sanskrit naming guidance — nakṣatra-based, gotra-aware, with meaning, prosody and cultural notes.",
        status: "dev",
    },
    {
        n: 9,
        name: "Sumvid Ghanam",
        sa: "संविद् घनम्",
        gloss: "dense knowledge",
        tag: "Knowledge Hub & Consultancy",
        blurb: "Curated knowledge hub and consultancy bridge — śāstra reference, scholar consultations, research collaboration.",
        status: "dev",
    },
    {
        n: 10,
        name: "CBS",
        sa: null,
        gloss: "Clinical Buddy Services",
        tag: "Bedside Medical Information",
        blurb: "Bedside medical information for doctors and caregivers — concise, reliable reference at the point of care.",
        status: "live",
        href: "https://vkg.works/apps/cbs/",
    },
    {
        n: 11,
        name: "Material Delivery",
        sa: null,
        gloss: "working name",
        tag: "Home · Pūjā · Event Supplies",
        blurb: "Sourcing and delivery of materials for home needs, pūjā kits and larger Dharmik events. Module name to be finalised.",
        status: "planned",
    },
    {
        n: 12,
        name: "Swadharma",
        sa: "स्वधर्म",
        gloss: "one's own duty",
        tag: "Temples · Events · Remote Services",
        blurb: "Temple setup, ritual planning, Dharmik event management, remote services and anything not covered by the other modules.",
        status: "dev",
    },
];

const STATUS_META = {
    this:    { label: "You are here",   tone: "saffron" },
    live:    { label: "Live",           tone: "success" },
    dev:     { label: "In development", tone: "gold"    },
    planned: { label: "Planned",        tone: "muted"   },
};

/**
 * RELATED — sister apps from the wider VKG / Vedic ecosystem that aren't
 * part of the 12 Swadharma service modules but share the same family.
 */
const RELATED = [
    {
        name: "Pranetaa",
        tag: "Digital Publisher",
        blurb: "Digital publishing platform for Sanskrit and Dharmik works — typesetting, distribution, mobile reading.",
        href: "https://pranetaa-mobile-sq7sy4rwza-el.a.run.app/",
    },
    {
        name: "Varna Krama",
        sa: "वर्णक्रम",
        tag: "Vedic Language Analyser",
        blurb: "Specialised analyser for Vedic phonology, varṇa-krama, accent patterns and recitation analysis.",
        href: "https://varnakrama-web.web.app/",
    },
    {
        name: "MAP OCR",
        tag: "PDF OCR Reader",
        blurb: "Optical character recognition for PDF files — extracting searchable text from scanned Sanskrit and Dharmik manuscripts and books.",
        href: "https://map-ocr-644387042032.asia-south1.run.app/viewer/",
    },
    {
        name: "Svāmī Exam Portal",
        sa: "स्वामी",
        tag: "Veda Examinations · End-to-End",
        blurb: "Full-stack portal for Veda examinations — student, school and scholar databases; applications, verification, hall tickets, personalised question papers, tabulation, certification, results publication, invitations and mailing lists. Built to a precise PDF design specification.",
        href: "https://exam.svami.in",
    },
];

const StatusPill = ({ status }) => {
    const meta = STATUS_META[status] || STATUS_META.planned;
    return <span className={`apps-pill apps-pill-${meta.tone}`}>{meta.label}</span>;
};

const SwadharmaApps = () => (
    <div className="swadharma-apps">

        <section className="apps-hero sw-aurora">
            <div className="apps-hero-inner">
                <Ornament variant="rule" tone="gold" size={160} className="apps-hero-rule" />
                <span className="apps-eyebrow">Swadharma · Service Modules</span>
                <h1 className="apps-title">
                    One family of apps for <em>every Dharmik need</em>
                </h1>
                <p className="apps-subtitle">
                    The Swadharma initiative is a connected suite of service modules — each
                    focused on a distinct domain, sharing a common identity, payments and
                    design language under <strong>Dharma Poshanam</strong>.
                </p>
            </div>
        </section>

        <section className="apps-section">
            <div className="sw-section-mark">Twelve service modules</div>
            <div className="apps-section-header">
                <h2>Apps in the Swadharma family</h2>
                <p className="apps-section-lead">
                    Live links open the module. Modules marked <em>In development</em> or
                    <em> Planned</em> will appear here as they ship — return visits update.
                </p>
            </div>

            <div className="apps-grid">
                {MODULES.map((m) => {
                    const isLinked = m.status === "this" || m.status === "live";
                    const isExternal = isLinked && /^https?:\/\//.test(m.href || "");
                    const Wrapper = !isLinked ? "div" : isExternal ? "a" : Link;
                    const wrapperProps = !isLinked
                        ? {}
                        : isExternal
                            ? { href: m.href, target: "_blank", rel: "noreferrer" }
                            : { to: m.href || "/" };
                    return (
                        <Wrapper
                            key={m.n}
                            className={`apps-card apps-card-${m.status}`}
                            {...wrapperProps}
                        >
                            <div className="apps-card-head">
                                <span className="apps-card-num">{String(m.n).padStart(2, "0")}</span>
                                <StatusPill status={m.status} />
                            </div>
                            <div className="apps-card-name">
                                <strong>{m.name}</strong>
                                {m.sa && (
                                    <span className="apps-card-sa">
                                        {m.sa}
                                        <span className="apps-card-gloss"> · {m.gloss}</span>
                                    </span>
                                )}
                                {!m.sa && m.gloss && (
                                    <span className="apps-card-sa apps-card-sa-en">{m.gloss}</span>
                                )}
                            </div>
                            <div className="apps-card-tag">{m.tag}</div>
                            <p className="apps-card-blurb">{m.blurb}</p>
                            {isLinked && (
                                <span className="apps-card-cta">
                                    <span>{m.status === "this" ? "You are here" : "Open"}</span>
                                    <Icon name="arrow" size={14} />
                                </span>
                            )}
                        </Wrapper>
                    );
                })}
            </div>
        </section>

        <section className="apps-section apps-related-section">
            <div className="sw-section-mark">Related apps</div>
            <div className="apps-section-header">
                <h2>From the wider VKG · Vedic family</h2>
                <p className="apps-section-lead">
                    Sister apps outside the strict Swadharma modules but built and
                    maintained by the same team — useful alongside the modules above.
                </p>
            </div>

            <div className="apps-grid apps-grid-related">
                {RELATED.map((r) => (
                    <a
                        key={r.name}
                        className="apps-card apps-card-live"
                        href={r.href}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <div className="apps-card-head">
                            <span className="apps-card-num">★</span>
                            <span className="apps-pill apps-pill-success">Live</span>
                        </div>
                        <div className="apps-card-name">
                            <strong>{r.name}</strong>
                            {r.sa && (
                                <span className="apps-card-sa">{r.sa}</span>
                            )}
                        </div>
                        <div className="apps-card-tag">{r.tag}</div>
                        <p className="apps-card-blurb">{r.blurb}</p>
                        <span className="apps-card-cta">
                            <span>Open</span>
                            <Icon name="arrow" size={14} />
                        </span>
                    </a>
                ))}
            </div>
        </section>

        <section className="apps-section apps-content-section">
            <div className="apps-content-card">
                <Ornament variant="lotus" tone="gold" size={48} />
                <h2>Content development continues</h2>
                <p>
                    Alongside the service modules, content development across Veda,
                    Sanskrit, Śāstra, Āyurveda and Dharmik culture continues uninterrupted —
                    feeding all modules and remaining freely available to the wider community.
                </p>
                <div className="apps-content-links">
                    <a href="https://vedavishtaram.in" target="_blank" rel="noreferrer" className="sw-btn sw-btn-navy sw-btn-sm">
                        <span>Vedavishtaram</span>
                        <Icon name="arrow" size={14} />
                    </a>
                    <a href="https://vedavishtaram.in/crj/index.html" target="_blank" rel="noreferrer" className="sw-btn sw-btn-navy sw-btn-sm">
                        <span>CRJ Resources</span>
                        <Icon name="arrow" size={14} />
                    </a>
                    <a href="https://dharmaposhanam.in" target="_blank" rel="noreferrer" className="sw-btn sw-btn-navy sw-btn-sm">
                        <span>Dharma Poshanam</span>
                        <Icon name="arrow" size={14} />
                    </a>
                    <Link to="/contact-us" className="sw-btn sw-btn-ghost sw-btn-sm">
                        <span>Contact / Collaborate</span>
                        <Icon name="arrow" size={14} />
                    </Link>
                </div>
            </div>
        </section>

    </div>
);

export default SwadharmaApps;
